/* eslint-disable object-curly-newline */
import { useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import SelectPicker from 'rsuite/SelectPicker';
import Input from 'rsuite/Input';
import { Button } from '@mui/material';
import { BaseModal } from '../BaseModal';
import { BackdropLoader } from '../../Loaders';
import { addCommas } from '../../../Helpers';
import { notificationServices } from '../../../Services';
import billPaymentsServices from '../../../Services/billPayments.services';

// Mirrors bill_payments_helpers.BILL_PAYMENT_MODES. Only a fallback — the modal reads
// the live list off the detail response so the dropdown cannot drift from the validator.
const FALLBACK_PAYMENT_MODES = [
	'NEFT',
	'RTGS',
	'IMPS',
	'UPI',
	'CHEQUE',
	'DEMAND_DRAFT',
	'CASH',
	'CARD',
	'NET_BANKING',
	'WALLET',
	'ADJUSTMENT',
	'OTHER',
];

// The backend refuses a payment on any other status, so the form is hidden rather than
// letting the user fill it in and be rejected.
const PAYABLE_STATUSES = ['UNPAID', 'PARTIALLY_PAID', 'PENDING', 'BOOKED', 'PAYMENT_INITIATED', 'PAYMENT_REJECTED'];

// A factory, not a const: `new Date()` evaluated at module load would leave the default
// payment date stuck on whenever the bundle loaded, which in a long-lived session is
// yesterday or older.
const emptyForm = () => {
	return {
		paymentDate: new Date(),
		paymentMode: '',
		paymentAmount: '',
		referenceNumber: '',
		notes: '',
	};
};

const asLabel = (mode) => {
	return mode?.split('_')?.join(' ');
};

const BillPaymentModal = (props) => {
	// mode='view' is a read-only history; mode='record' adds the entry form on top of it.
	const { show, bill, mode = 'view', handleClose, onPaymentSaved } = props;
	const isRecordMode = mode === 'record';
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [detail, setDetail] = useState(null);
	const [payments, setPayments] = useState([]);
	const [paymentModes, setPaymentModes] = useState(FALLBACK_PAYMENT_MODES);
	const [form, setForm] = useState(emptyForm());

	const clientBillId = bill?.clientTransactionId;
	const billRowId = bill?.id;

	const modeOptions = useMemo(() => {
		return paymentModes?.map((paymentMode) => {
			return {
				label: asLabel(paymentMode),
				value: paymentMode,
			};
		});
	}, [paymentModes]);

	const fetchDetail = useCallback(async () => {
		if (!clientBillId && !billRowId) {
			return;
		}
		setLoading(true);
		try {
			const response = await billPaymentsServices.fetchBillPaymentDetail({
				data: {
					billId: clientBillId,
					billRowId: clientBillId ? undefined : billRowId,
				},
			});
			if (response?.statusCode === 200) {
				setDetail(response?.data?.bill ?? null);
				setPayments(response?.data?.payments ?? []);
				if (response?.data?.paymentModes?.length) {
					setPaymentModes(response?.data?.paymentModes);
				}
			}
		} catch (error) {
			// eslint-disable-next-line no-console
			console.log(error);
		} finally {
			setLoading(false);
		}
	}, [clientBillId, billRowId]);

	useEffect(() => {
		if (show) {
			setForm(emptyForm());
			fetchDetail();
		} else {
			setDetail(null);
			setPayments([]);
		}
	}, [show, fetchDetail]);

	const outstanding = detail?.outstandingAmount ?? 0;
	const canAcceptPayment = PAYABLE_STATUSES.includes(detail?.billStatus) && outstanding > 0;

	const validate = () => {
		if (!form?.paymentDate) {
			return 'Payment date is required';
		}
		if (moment(form?.paymentDate).startOf('day').isAfter(moment().startOf('day'))) {
			return 'Payment date cannot be in the future';
		}
		if (!form?.paymentMode) {
			return 'Payment mode is required';
		}
		const amount = Number(form?.paymentAmount);
		if (!form?.paymentAmount || Number.isNaN(amount) || amount <= 0) {
			return 'Payment amount must be greater than zero';
		}
		// Guarded here too so a typo is caught before the round trip; the backend is
		// still the authority, since another payment may land in between.
		if (amount > outstanding + 0.01) {
			return `Payment amount cannot exceed the outstanding amount of ₹${addCommas(outstanding)}`;
		}
		return null;
	};

	const handleSubmit = async () => {
		const error = validate();
		if (error) {
			notificationServices.generateNotification({
				type: 'error',
				message: error,
			});
			return;
		}
		setSaving(true);
		try {
			const response = await billPaymentsServices.createBillPayment({
				data: {
					billId: clientBillId,
					billRowId: clientBillId ? undefined : billRowId,
					paymentMode: form?.paymentMode,
					paymentAmount: Number(form?.paymentAmount),
					referenceNumber: form?.referenceNumber?.trim() || null,
					paymentDate: moment(form?.paymentDate).format('YYYY-MM-DD HH:mm:ss'),
					notes: form?.notes?.trim() || null,
				},
			});
			if (response?.statusCode === 200) {
				notificationServices.generateNotification({
					type: 'success',
					message: response?.message,
				});
				setForm(emptyForm());
				await fetchDetail();
				onPaymentSaved?.();
			}
		} catch (err) {
			// eslint-disable-next-line no-console
			console.log(err);
		} finally {
			setSaving(false);
		}
	};

	const blockedStateLabel = asLabel(detail?.billStatus) ?? 'in this state';
	const blockedReason =
		detail?.billStatus === 'PAID'
			? 'This bill is fully paid. No further payment can be added.'
			: `A payment cannot be added while the bill is ${blockedStateLabel}.`;

	const summaryTiles = [
		{ label: 'Bill Amount', value: `₹${addCommas(detail?.billAmount ?? 0)}` },
		{ label: 'Paid Amount', value: `₹${addCommas(detail?.paidAmount ?? 0)}` },
		{ label: 'Outstanding', value: `₹${addCommas(outstanding)}` },
		{ label: 'Bill Status', value: asLabel(detail?.billStatus) ?? '-' },
	];

	return (
		<BaseModal
			open={show}
			handleClose={handleClose}
			heading={`${isRecordMode ? 'Record Payment' : 'View Payment Info'} — Bill ${
				clientBillId ?? billRowId ?? ''
			}`}
			width='60rem'
			height='90%'
			showClose
			hideFooter
			aria={{
				label: 'bill payment info',
				description: 'View and add payment information against a bill',
			}}
		>
			<BackdropLoader showProgress isLoading={loading || saving} />
			<div className='flex flex-col gap-4 overflow-y-auto px-1 pb-2'>
				<div className='grid grid-cols-4 gap-3'>
					{summaryTiles.map((tile) => {
						return (
							<div
								key={tile.label}
								className='flex flex-col gap-1 rounded-md border border-gray-200 bg-gray-50 p-3'
							>
								<span className='text-xs text-gray-500'>{tile.label}</span>
								<span className='text-sm font-semibold'>{tile.value}</span>
							</div>
						);
					})}
				</div>
				<div className='flex gap-6 text-sm text-gray-600'>
					<span className='flex gap-1'>
						<span>Vendor:</span>
						<b className='text-gray-900'>{detail?.vendorName ?? '-'}</b>
					</span>
					<span className='flex gap-1'>
						<span>Store:</span>
						<b className='text-gray-900'>{detail?.storeName ?? '-'}</b>
					</span>
				</div>

				{isRecordMode && canAcceptPayment ? (
					<div className='rounded-md border border-gray-200 p-4'>
						<div className='mb-3 text-sm font-semibold'>Add Payment</div>
						<div className='grid grid-cols-3 gap-4'>
							<div className='flex flex-col gap-1'>
								<label className='flex gap-1 text-xs text-gray-600' htmlFor='billPaymentDate'>
									<span>Payment Date</span>
									<span className='text-red-500'>*</span>
								</label>
								<input
									id='billPaymentDate'
									type='date'
									className='h-9 rounded-md border border-gray-300 px-2 text-sm'
									max={moment().format('YYYY-MM-DD')}
									value={
										form?.paymentDate
											? moment(form.paymentDate).format('YYYY-MM-DD')
											: ''
									}
									onChange={(e) => {
										const { value } = e.target;
										setForm((prev) => {
											return {
												...prev,
												paymentDate: value ? new Date(value) : null,
											};
										});
									}}
								/>
							</div>
							<div className='flex flex-col gap-1'>
								<label className='flex gap-1 text-xs text-gray-600' htmlFor='billPaymentMode'>
									<span>Payment Mode</span>
									<span className='text-red-500'>*</span>
								</label>
								<SelectPicker
									id='billPaymentMode'
									data={modeOptions}
									searchable={false}
									cleanable={false}
									value={form?.paymentMode}
									placeholder='Select mode'
									menuStyle={{ zIndex: 9999 }}
									onChange={(value) => {
										setForm((prev) => {
											return { ...prev, paymentMode: value };
										});
									}}
								/>
							</div>
							<div className='flex flex-col gap-1'>
								<label className='flex gap-1 text-xs text-gray-600' htmlFor='billPaymentAmount'>
									<span>Payment Amount</span>
									<span className='text-red-500'>*</span>
								</label>
								<Input
									id='billPaymentAmount'
									type='number'
									value={form?.paymentAmount}
									placeholder={`Max ₹${addCommas(outstanding)}`}
									onChange={(value) => {
										setForm((prev) => {
											return { ...prev, paymentAmount: value };
										});
									}}
								/>
							</div>
							<div className='flex flex-col gap-1'>
								<label className='text-xs text-gray-600' htmlFor='billPaymentRef'>
									Reference Number
								</label>
								<Input
									id='billPaymentRef'
									value={form?.referenceNumber}
									placeholder='UTR / cheque / txn reference'
									onChange={(value) => {
										setForm((prev) => {
											return { ...prev, referenceNumber: value };
										});
									}}
								/>
							</div>
							<div className='col-span-2 flex flex-col gap-1'>
								<label className='text-xs text-gray-600' htmlFor='billPaymentNotes'>
									Notes
								</label>
								<Input
									id='billPaymentNotes'
									as='textarea'
									rows={2}
									value={form?.notes}
									placeholder='Any remark against this payment'
									onChange={(value) => {
										setForm((prev) => {
											return { ...prev, notes: value };
										});
									}}
								/>
							</div>
						</div>
						<div className='mt-4 flex justify-end'>
							<Button
								variant='contained'
								color='primary'
								disabled={saving}
								onClick={handleSubmit}
							>
								Save Payment
							</Button>
						</div>
					</div>
				) : null}
				{isRecordMode && !canAcceptPayment ? (
					<div className='rounded-md border border-gray-200 bg-gray-50 p-3 text-sm text-gray-600'>
						{blockedReason}
					</div>
				) : null}

				<div>
					<div className='mb-2 text-sm font-semibold'>
						{`Payment History (${payments?.length ?? 0})`}
					</div>
					<div className='overflow-x-auto rounded-md border border-gray-200'>
						<table className='w-full text-left text-sm'>
							<thead className='bg-gray-100 text-xs uppercase text-gray-600'>
								<tr>
									<th className='px-3 py-2'>Payment Date</th>
									<th className='px-3 py-2'>Payment Mode</th>
									<th className='px-3 py-2'>Amount</th>
									<th className='px-3 py-2'>Reference No</th>
									<th className='px-3 py-2'>Notes</th>
									<th className='px-3 py-2'>Recorded By</th>
									<th className='px-3 py-2'>Source</th>
									<th className='px-3 py-2'>Recorded At</th>
								</tr>
							</thead>
							<tbody>
								{payments?.length ? (
									payments.map((payment) => {
										return (
											<tr key={payment?.id} className='border-t border-gray-200'>
												<td className='px-3 py-2'>
													{payment?.paymentDate
														? moment(payment.paymentDate).format('DD-MM-YYYY')
														: '-'}
												</td>
												<td className='px-3 py-2'>{asLabel(payment?.paymentMode) ?? '-'}</td>
												<td className='px-3 py-2'>{`₹${addCommas(payment?.paymentAmount ?? 0)}`}</td>
												<td className='px-3 py-2'>{payment?.referenceNumber ?? '-'}</td>
												<td className='px-3 py-2'>{payment?.notes ?? '-'}</td>
												<td className='px-3 py-2'>{payment?.recordedByName ?? '-'}</td>
												<td className='px-3 py-2'>{payment?.source ?? '-'}</td>
												<td className='px-3 py-2'>
													{payment?.createdAt
														? moment(payment.createdAt).format('DD-MM-YYYY HH:mm')
														: '-'}
												</td>
											</tr>
										);
									})
								) : (
									<tr>
										<td className='px-3 py-4 text-gray-500' colSpan={8}>
											No payments recorded against this bill yet.
										</td>
									</tr>
								)}
							</tbody>
						</table>
					</div>
				</div>
			</div>
		</BaseModal>
	);
};

export default BillPaymentModal;
